import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getToken } from "../../features/user/UserAvatar";
import { error, failedResponse, getBackendUrl, loaded, loading, LoadState, LoadStateInfo, notStarted } from "../common";
import { routineCommentChange } from "./routineCommentChangeSlice";
import { RoutineCommentInfo } from "./routineCommentSlice";

export interface RoutineCommentListState {
    state: LoadStateInfo;
    request?: RetrieveRoutineCommentListRequest;
    list?: RoutineCommentInfo[];
}

const initialState: RoutineCommentListState = {
    state: notStarted(),
};


export interface RetrieveRoutineCommentListRequest {
    routineId: string;
    eventDate: string;
    classroomId: string;
}
export const retrieveRoutineCommentList = createAsyncThunk(
    "retrieveRoutineCommentList",
    async (param: RetrieveRoutineCommentListRequest, thunkAPI) => {
        console.debug(`retrieve routine comments for ${param.routineId}, classroom ${param.classroomId}, date ${param.eventDate}`);

        const response = await fetch(`${getBackendUrl()}/api/routine/${param.routineId}/comments/${param.eventDate}/${param.classroomId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveRoutineCommentListRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.routineCommentList;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveRoutineCommentList skipped: ${status.state.state}`);
            return false;
        }
    }
);

export function findRoutineComment(comments: RoutineCommentInfo[] | undefined, routineId: string, classroomId: string, studentId: string, eventDate: string): RoutineCommentInfo | undefined {
    if (!comments) {
        return undefined;
    }
    console.log(`Finding routine comment for routineId=${routineId}, classroomId=${classroomId}, studentId=${studentId}, eventDate=${eventDate} in comments:`, comments);
    return comments.find((c) =>
        c.routineId == routineId &&
        c.classroomId == classroomId &&
        c.studentId == studentId &&
        c.eventDate == eventDate
    );
}

export const routineCommentListSlice = createSlice({
    name: "routineCommentList",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        check: (state, action: PayloadAction<RetrieveRoutineCommentListRequest>) => {
            if (state.state.state != LoadState.NotStarted
                && (state.request && state.request.routineId != action.payload.routineId)
            ) {
                console.debug(`routine comment list not retrieved for routine ${action.payload.routineId}`);
                state.state = notStarted();
                state.request = action.payload;
                state.list = undefined;
            }
        },
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveRoutineCommentList.pending, (state, action) => {
                state.state = loading();
                state.list = undefined;
                state.request = action.meta.arg;
            })
            .addCase(retrieveRoutineCommentList.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
                // state.list?.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
            })
            .addCase(retrieveRoutineCommentList.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(routineCommentChange.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                const payload: RoutineCommentInfo = action.payload;
                if (state.list) {
                    const index = state.list.findIndex((r) => r.id === payload.id);
                    if (index >= 0) {
                        state.list[index] = payload;
                    } else {
                        state.list.push(payload);
                    }
                }
            })
            ;
    },
});

// export const { } = routineCommentListSlice.actions;

export const selectRoutineCommentList = (state: RootState) => state.routineCommentList;

export default routineCommentListSlice.reducer;
